import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { useTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Globe, Palette, Bell, LayoutGrid, Save, Eye, EyeOff, User, LayoutDashboard, CreditCard, Check, AlertTriangle, Zap, Crown, Star, Calendar, BedDouble } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';

const currencies = [
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
  { value: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { value: 'CHF', label: 'Swiss Franc (CHF)', symbol: 'CHF' },
];

const timezones = [
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'America/New_York', label: 'America/New York (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'UTC', label: 'UTC' },
];

const ALL_MODULES = [
  { id: 'dashboard', name: 'Dashboard', description: 'Main overview and statistics', required: true },
  { id: 'calendar', name: 'Calendar', description: 'Room availability calendar view' },
  { id: 'rooms', name: 'Rooms', description: 'Room inventory and management' },
  { id: 'reservations', name: 'Reservations', description: 'Booking management' },
  { id: 'guests', name: 'Guests', description: 'Guest profiles and history' },
  { id: 'billing', name: 'Billing', description: 'Invoices and payments' },
  { id: 'expenses', name: 'Expenses', description: 'Track hotel expenses' },
  { id: 'housekeeping', name: 'Housekeeping', description: 'Cleaning tasks' },
  { id: 'maintenance', name: 'Maintenance', description: 'Maintenance requests' },
  { id: 'transfers', name: 'Transfers', description: 'Airport/city transfers' },
  { id: 'channels', name: 'Channels', description: 'OTA integrations (Booking.com, Expedia)' },
  { id: 'reports', name: 'Reports', description: 'Analytics and reports' },
  { id: 'staff', name: 'Staff', description: 'Team management' },
  { id: 'settings', name: 'Settings', description: 'System configuration', required: true },
];

// Dashboard widgets configuration
const DASHBOARD_WIDGETS = [
  { id: 'quick_stats', name: 'Quick Stats', description: 'Occupancy, Arrivals, Departures, In-House, Profit' },
  { id: 'todays_arrivals', name: "Today's Arrivals", description: 'List of guests arriving today with check-in buttons' },
  { id: 'room_status', name: 'Room Status', description: 'Visual grid showing all room statuses' },
  { id: 'todays_departures', name: "Today's Departures", description: 'List of guests departing today with check-out buttons' },
  { id: 'tasks_overview', name: 'Tasks Overview', description: 'Pending cleaning and maintenance tasks' },
];

// Room status options configuration
const ROOM_STATUSES = [
  { id: 'available', name: 'Available', description: 'Room is ready for booking', color: 'bg-emerald-500' },
  { id: 'occupied', name: 'Occupied', description: 'Guest currently staying', color: 'bg-red-500' },
  { id: 'dirty', name: 'Dirty', description: 'Needs cleaning after checkout', color: 'bg-amber-500' },
  { id: 'clean', name: 'Clean', description: 'Cleaned and inspected', color: 'bg-teal-500' },
  { id: 'out_of_service', name: 'Out of Service', description: 'Temporarily unavailable', color: 'bg-rose-500' },
  { id: 'maintenance', name: 'Maintenance', description: 'Under repair or maintenance', color: 'bg-orange-500' },
];

const SettingsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { hotel, hotelSettings, fetchHotel, updateHotelSettings } = useHotel();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [guestFieldsLoading, setGuestFieldsLoading] = useState(false);
  const [dashboardWidgetsLoading, setDashboardWidgetsLoading] = useState(false);
  const [roomStatusesLoading, setRoomStatusesLoading] = useState(false);
  const [visibleModules, setVisibleModules] = useState([]);
  const [dashboardWidgets, setDashboardWidgets] = useState({
    quick_stats: true,
    todays_arrivals: true,
    room_status: true,
    todays_departures: true,
    tasks_overview: true
  });
  const [guestFields, setGuestFields] = useState({
    email: true,
    phone: true,
    address: true,
    nationality: true,
    id_type: true,
    id_number: true,
    notes: true
  });
  const [roomStatuses, setRoomStatuses] = useState({
    available: true,
    occupied: true,
    dirty: true,
    clean: true,
    out_of_service: true,
    maintenance: true
  });
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);
  
  useEffect(() => {
    // Initialize visible modules - if not set or empty, default to all modules
    if (hotelSettings?.visible_modules && hotelSettings.visible_modules.length > 0) {
      setVisibleModules(hotelSettings.visible_modules);
    } else {
      // Default to all modules visible
      setVisibleModules(ALL_MODULES.map(m => m.id));
    }
    if (hotelSettings?.guest_fields) {
      setGuestFields(hotelSettings.guest_fields);
    }
    if (hotelSettings?.dashboard_widgets) {
      setDashboardWidgets(hotelSettings.dashboard_widgets);
    }
    if (hotelSettings?.room_statuses) {
      setRoomStatuses(hotelSettings.room_statuses);
    }
  }, [hotelSettings]);
  
  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const [subRes, plansRes] = await Promise.all([
          axios.get(`${API}/subscription`, { headers: getAuthHeaders() }),
          axios.get(`${API}/subscription/plans`)
        ]);
        setSubscription(subRes.data);
        setSubscriptionPlans(plansRes.data);
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscription();
  }, [getAuthHeaders]);
  
  const [hotelForm, setHotelForm] = useState({
    name: hotel?.name || '',
    address: hotel?.address || '',
    city: hotel?.city || '',
    country: hotel?.country || '',
    phone: hotel?.phone || '',
    email: hotel?.email || '',
    timezone: hotel?.timezone || 'Europe/Paris',
    currency: hotel?.currency || 'EUR'
  });

  const handleSaveHotel = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API}/hotels/${user.hotel_id}`,
        hotelForm,
        { headers: getAuthHeaders() }
      );
      await fetchHotel();
      toast.success('Hotel settings saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    const module = ALL_MODULES.find(m => m.id === moduleId);
    if (module?.required) return;
    
    setVisibleModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleSaveModules = async () => {
    setModuleLoading(true);
    try {
      const success = await updateHotelSettings({ ...hotelSettings, visible_modules: visibleModules });
      if (success) {
        toast.success('Module visibility saved');
      } else {
        toast.error('Failed to save module settings');
      }
    } catch (error) {
      toast.error('Failed to save module settings');
    } finally {
      setModuleLoading(false);
    }
  };

  const toggleGuestField = (field) => {
    setGuestFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveGuestFields = async () => {
    setGuestFieldsLoading(true);
    try {
      const success = await updateHotelSettings({ ...hotelSettings, guest_fields: guestFields });
      if (success) {
        toast.success('Guest field settings saved');
      } else {
        toast.error('Failed to save guest field settings');
      }
    } catch (error) {
      toast.error('Failed to save guest field settings');
    } finally {
      setGuestFieldsLoading(false);
    }
  };

  const toggleDashboardWidget = (widgetId) => {
    setDashboardWidgets(prev => ({
      ...prev,
      [widgetId]: !prev[widgetId]
    }));
  };

  const handleSaveDashboardWidgets = async () => {
    setDashboardWidgetsLoading(true);
    try {
      const success = await updateHotelSettings({ ...hotelSettings, dashboard_widgets: dashboardWidgets });
      if (success) {
        toast.success('Dashboard widget settings saved');
      } else {
        toast.error('Failed to save dashboard settings');
      }
    } catch (error) {
      toast.error('Failed to save dashboard settings');
    } finally {
      setDashboardWidgetsLoading(false);
    }
  };

  const handleSaveRoomStatuses = async () => {
    // Ensure at least 'available' and 'occupied' are enabled
    if (!roomStatuses.available || !roomStatuses.occupied) {
      toast.error('Available and Occupied statuses are required');
      return;
    }
    setRoomStatusesLoading(true);
    try {
      const success = await updateHotelSettings({ ...hotelSettings, room_statuses: roomStatuses });
      if (success) {
        toast.success('Room status settings saved');
      } else {
        toast.error('Failed to save room status settings');
      }
    } catch (error) {
      toast.error('Failed to save room status settings');
    } finally {
      setRoomStatusesLoading(false);
    }
  };

  // Stripe Payment Links — direct hosted checkout
  const PAYMENT_LINKS = {
    starter: {
      monthly: 'https://buy.stripe.com/00w6oycTA8i4ay7bOdfw400',  // €29/month
      yearly:  'https://buy.stripe.com/fZu14e9Ho41Oay7aK9fw401'   // €290/year
    },
    professional: {
      monthly: 'https://buy.stripe.com/8x2fZ85r8cykfSr05vfw402',  // €69/month
      yearly:  'https://buy.stripe.com/9B6cMW4n455SgWv05vfw403'   // €690/year
    },
    premium: {
      monthly: 'https://buy.stripe.com/14A8wG3j041O5dN3hHfw404',  // €129/month
      yearly:  'https://buy.stripe.com/bJe7sCaLsaqc8pZ3hHfw405'   // €1,290/year
    }
  };

  const handleUpgrade = () => {
    if (!selectedPlan) return;
    const links = PAYMENT_LINKS[selectedPlan];
    if (links) {
      const url = selectedBillingCycle === 'yearly' ? links.yearly : links.monthly;
      // Append prefill email so Stripe pre-fills the customer email
      const fullUrl = user?.email ? `${url}?prefilled_email=${encodeURIComponent(user.email)}` : url;
      window.open(fullUrl, '_blank');
      setUpgradeDialogOpen(false);
      toast.success('Redirecting to Stripe checkout... Your subscription will activate automatically after payment.');
    } else {
      // Fallback to API checkout for plans without direct links
      setUpgrading(true);
      axios.post(
        `${API}/subscription/checkout`,
        { plan: selectedPlan, billing_cycle: selectedBillingCycle, origin_url: window.location.origin },
        { headers: getAuthHeaders() }
      ).then(response => {
        if (response.data.url) window.location.href = response.data.url;
        else throw new Error('No checkout URL received');
      }).catch(error => {
        toast.error(getErrorMessage(error, 'Failed to initiate checkout'));
        setUpgrading(false);
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) return;
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeaders() });
      toast.success('Subscription canceled');
      const subRes = await axios.get(`${API}/subscription`, { headers: getAuthHeaders() });
      setSubscription(subRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel subscription'));
    }
  };

  const getPlanPrice = (planKey, cycle) => {
    const plan = subscriptionPlans[planKey];
    if (!plan) return 0;
    return cycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  };

  const formatPrice = (price, cycle) => {
    if (price === 0) return 'Free';
    return `€${price}/${cycle === 'yearly' ? 'year' : 'month'}`;
  };

  const GUEST_FIELDS = [
    { id: 'email', name: 'Email', description: 'Guest email address' },
    { id: 'phone', name: 'Phone', description: 'Contact phone number' },
    { id: 'address', name: 'Address', description: 'Home address' },
    { id: 'nationality', name: 'Nationality', description: 'Country of origin' },
    { id: 'id_type', name: 'ID Type', description: 'Passport, ID Card, etc.' },
    { id: 'id_number', name: 'ID Number', description: 'Document number' },
    { id: 'notes', name: 'Notes', description: 'Additional notes about guest' },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit']">Settings</h1>
        <p className="text-muted-foreground">Manage hotel and system preferences</p>
      </div>

      <Tabs defaultValue="hotel" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="hotel">Hotel Profile</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
          <TabsTrigger value="dashboard-widgets">Dashboard Widgets</TabsTrigger>
          <TabsTrigger value="room-statuses">Room Statuses</TabsTrigger>
          <TabsTrigger value="guest-fields">Guest Fields</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* Hotel Profile */}
        <TabsContent value="hotel">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Hotel Information
              </CardTitle>
              <CardDescription>
                Update your hotel's basic information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveHotel} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Hotel Name</Label>
                    <Input
                      value={hotelForm.name}
                      onChange={(e) => setHotelForm({ ...hotelForm, name: e.target.value })}
                      data-testid="settings-hotel-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={hotelForm.email}
                      onChange={(e) => setHotelForm({ ...hotelForm, email: e.target.value })}
                      data-testid="settings-hotel-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={hotelForm.phone}
                      onChange={(e) => setHotelForm({ ...hotelForm, phone: e.target.value })}
                      data-testid="settings-hotel-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={hotelForm.address}
                      onChange={(e) => setHotelForm({ ...hotelForm, address: e.target.value })}
                      data-testid="settings-hotel-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={hotelForm.city}
                      onChange={(e) => setHotelForm({ ...hotelForm, city: e.target.value })}
                      data-testid="settings-hotel-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={hotelForm.country}
                      onChange={(e) => setHotelForm({ ...hotelForm, country: e.target.value })}
                      data-testid="settings-hotel-country"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Timezone
                    </Label>
                    <Select
                      value={hotelForm.timezone}
                      onValueChange={(value) => setHotelForm({ ...hotelForm, timezone: value })}
                    >
                      <SelectTrigger data-testid="settings-timezone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select
                      value={hotelForm.currency}
                      onValueChange={(value) => setHotelForm({ ...hotelForm, currency: value })}
                    >
                      <SelectTrigger data-testid="settings-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem key={curr.value} value={curr.value}>
                            {curr.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button type="submit" disabled={loading} data-testid="save-hotel-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription">
          <div className="space-y-6">
            {/* Current Plan Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Current Plan
                </CardTitle>
                <CardDescription>
                  Manage your subscription and billing
                </CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptionLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : subscription ? (
                  <div className="space-y-6">
                    {/* Plan Status Banner */}
                    <div className={`p-4 rounded-lg ${
                      subscription.status === 'trial' ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800' :
                      subscription.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800' :
                      'bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {subscription.plan === 'trial' ? (
                            <Calendar className="h-6 w-6 text-blue-600" />
                          ) : subscription.plan === 'starter' ? (
                            <Star className="h-6 w-6 text-amber-600" />
                          ) : subscription.plan === 'professional' ? (
                            <Zap className="h-6 w-6 text-purple-600" />
                          ) : (
                            <Crown className="h-6 w-6 text-amber-500" />
                          )}
                          <div>
                            <h3 className="font-semibold text-lg capitalize">
                              {subscription.plan_config?.name || subscription.plan}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {subscription.status === 'trial' ? (
                                subscription.is_trial_expired ? (
                                  <span className="text-red-600 font-medium">Trial expired</span>
                                ) : (
                                  <span>{subscription.days_remaining} days remaining in trial</span>
                                )
                              ) : subscription.status === 'active' ? (
                                <span className="text-emerald-600">Active subscription</span>
                              ) : (
                                <span className="text-amber-600 capitalize">{subscription.status}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {subscription.status === 'trial' || subscription.status === 'expired' ? (
                          <Button onClick={() => { setSelectedPlan('starter'); setUpgradeDialogOpen(true); }} data-testid="upgrade-plan-btn">
                            Upgrade Now
                          </Button>
                        ) : subscription.status === 'active' && subscription.plan !== 'premium' ? (
                          <Button variant="outline" onClick={() => { setSelectedPlan(subscription.plan === 'starter' ? 'professional' : 'premium'); setUpgradeDialogOpen(true); }}>
                            Change Plan
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {/* Trial Warning */}
                    {subscription.status === 'trial' && subscription.days_remaining <= 7 && (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
                        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">
                            {subscription.is_trial_expired 
                              ? 'Your trial has expired' 
                              : `Only ${subscription.days_remaining} days left in your trial`}
                          </p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            {subscription.is_trial_expired 
                              ? 'You can still view your data, but cannot create new reservations, guests, or rooms. Upgrade to continue.'
                              : 'Upgrade now to ensure uninterrupted access to your hotel management system.'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Usage Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">Hotels</span>
                          <span className="font-medium">
                            {subscription.hotels_count} / {subscription.plan_config?.max_hotels === -1 ? '∞' : subscription.plan_config?.max_hotels}
                          </span>
                        </div>
                        <Progress 
                          value={subscription.plan_config?.max_hotels === -1 ? 10 : (subscription.hotels_count / subscription.plan_config?.max_hotels) * 100} 
                          className="h-2"
                        />
                      </div>
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">Rooms</span>
                          <span className="font-medium">
                            {subscription.rooms_count} / {subscription.plan_config?.max_rooms_per_hotel === -1 ? '∞' : subscription.plan_config?.max_rooms_per_hotel * Math.max(1, subscription.hotels_count)}
                          </span>
                        </div>
                        <Progress 
                          value={subscription.plan_config?.max_rooms_per_hotel === -1 ? 10 : (subscription.rooms_count / (subscription.plan_config?.max_rooms_per_hotel * Math.max(1, subscription.hotels_count))) * 100} 
                          className="h-2"
                        />
                      </div>
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">Staff</span>
                          <span className="font-medium">
                            {subscription.staff_count} / {subscription.plan_config?.max_staff === -1 ? '∞' : subscription.plan_config?.max_staff}
                          </span>
                        </div>
                        <Progress 
                          value={subscription.plan_config?.max_staff === -1 ? 10 : (subscription.staff_count / subscription.plan_config?.max_staff) * 100} 
                          className="h-2"
                        />
                      </div>
                    </div>

                    {/* Plan Features */}
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Included Features</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {subscription.plan_config?.features?.map((feature) => (
                          <div key={feature} className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-emerald-600" />
                            <span className="capitalize">{feature.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Cancel Subscription */}
                    {subscription.status === 'active' && (
                      <div className="pt-4 border-t">
                        <Button variant="ghost" className="text-destructive" onClick={handleCancelSubscription}>
                          Cancel Subscription
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Unable to load subscription data</p>
                )}
              </CardContent>
            </Card>

            {/* Available Plans */}
            <Card>
              <CardHeader>
                <CardTitle>Available Plans</CardTitle>
                <CardDescription>Choose the plan that fits your needs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Starter Plan */}
                  <div className={`border rounded-lg p-6 ${subscription?.plan === 'starter' ? 'border-primary ring-2 ring-primary/20' : ''}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-5 w-5 text-amber-500" />
                      <h3 className="font-semibold text-lg">Starter</h3>
                    </div>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">€29</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Or €290/year (save €58)</p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        1 hotel
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 20 rooms
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 5 staff
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Core ERP features
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Manual transfers
                      </li>
                    </ul>
                    {subscription?.plan === 'starter' ? (
                      <Badge className="w-full justify-center">Current Plan</Badge>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => { setSelectedPlan('starter'); setUpgradeDialogOpen(true); }}
                      >
                        Select Plan
                      </Button>
                    )}
                  </div>

                  {/* Professional Plan */}
                  <div className={`border rounded-lg p-6 relative ${subscription?.plan === 'professional' ? 'border-primary ring-2 ring-primary/20' : 'border-purple-200 dark:border-purple-800'}`}>
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-purple-600">Most Popular</Badge>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="h-5 w-5 text-purple-600" />
                      <h3 className="font-semibold text-lg">Professional</h3>
                    </div>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">€69</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Or €690/year (save €138)</p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 3 hotels
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 80 rooms per hotel
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 15 staff
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Advanced reports
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Drivers & vehicles
                      </li>
                    </ul>
                    {subscription?.plan === 'professional' ? (
                      <Badge className="w-full justify-center">Current Plan</Badge>
                    ) : (
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => { setSelectedPlan('professional'); setUpgradeDialogOpen(true); }}
                      >
                        Select Plan
                      </Button>
                    )}
                  </div>

                  {/* Premium Plan */}
                  <div className={`border rounded-lg p-6 ${subscription?.plan === 'premium' ? 'border-primary ring-2 ring-primary/20' : 'border-amber-200 dark:border-amber-800'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="h-5 w-5 text-amber-500" />
                      <h3 className="font-semibold text-lg">Premium</h3>
                    </div>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">€129</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Or €1,290/year (save €258)</p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Up to 10 hotels
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Unlimited rooms
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Unlimited staff
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Accounting exports
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600" />
                        API access
                      </li>
                    </ul>
                    {subscription?.plan === 'premium' ? (
                      <Badge className="w-full justify-center">Current Plan</Badge>
                    ) : (
                      <Button 
                        variant="outline"
                        className="w-full border-amber-500 text-amber-600 hover:bg-amber-50"
                        onClick={() => { setSelectedPlan('premium'); setUpgradeDialogOpen(true); }}
                      >
                        Select Plan
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Modules */}
        <TabsContent value="modules">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutGrid className="h-5 w-5" />
                Module Visibility
              </CardTitle>
              <CardDescription>
                Choose which modules appear in the sidebar. Required modules cannot be disabled.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ALL_MODULES.map((module) => {
                  const isVisible = visibleModules.includes(module.id);
                  const isRequired = module.required;
                  
                  return (
                    <div
                      key={module.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isVisible ? 'bg-muted/50 border-primary/30' : 'bg-background'
                      } ${isRequired ? 'opacity-75' : ''}`}
                      data-testid={`module-toggle-${module.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {isVisible ? (
                          <Eye className="h-5 w-5 text-primary" />
                        ) : (
                          <EyeOff className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div>
                          <p className="font-medium">{module.name}</p>
                          <p className="text-sm text-muted-foreground">{module.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isVisible}
                        onCheckedChange={() => toggleModule(module.id)}
                        disabled={isRequired}
                        data-testid={`module-switch-${module.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              
              <Button onClick={handleSaveModules} disabled={moduleLoading} data-testid="save-modules-btn">
                <Save className="h-4 w-4 mr-2" />
                {moduleLoading ? 'Saving...' : 'Save Module Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Widgets */}
        <TabsContent value="dashboard-widgets">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5" />
                Dashboard Widgets
              </CardTitle>
              <CardDescription>
                Choose which sections appear on the Front Desk Dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DASHBOARD_WIDGETS.map((widget) => {
                  const isVisible = dashboardWidgets[widget.id] !== false;
                  
                  return (
                    <div
                      key={widget.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isVisible ? 'bg-muted/50 border-primary/30' : 'bg-background'
                      }`}
                      data-testid={`widget-toggle-${widget.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {isVisible ? (
                          <Eye className="h-5 w-5 text-primary" />
                        ) : (
                          <EyeOff className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div>
                          <p className="font-medium">{widget.name}</p>
                          <p className="text-sm text-muted-foreground">{widget.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isVisible}
                        onCheckedChange={() => toggleDashboardWidget(widget.id)}
                        data-testid={`widget-switch-${widget.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              
              <Button onClick={handleSaveDashboardWidgets} disabled={dashboardWidgetsLoading} data-testid="save-dashboard-widgets-btn">
                <Save className="h-4 w-4 mr-2" />
                {dashboardWidgetsLoading ? 'Saving...' : 'Save Dashboard Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Room Statuses */}
        <TabsContent value="room-statuses">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BedDouble className="h-5 w-5" />
                Room Status Options
              </CardTitle>
              <CardDescription>
                Enable or disable room status options. Available and Occupied are required.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                {ROOM_STATUSES.map((status) => {
                  const isRequired = status.id === 'available' || status.id === 'occupied';
                  return (
                    <div
                      key={status.id}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        roomStatuses[status.id] ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-950/20' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded ${status.color}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{status.name}</p>
                            {isRequired && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{status.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={roomStatuses[status.id]}
                        onCheckedChange={(checked) => setRoomStatuses({ ...roomStatuses, [status.id]: checked })}
                        disabled={isRequired}
                        data-testid={`room-status-${status.id}-switch`}
                      />
                    </div>
                  );
                })}
              </div>
              <Button onClick={handleSaveRoomStatuses} disabled={roomStatusesLoading} data-testid="save-room-statuses-btn">
                <Save className="h-4 w-4 mr-2" />
                {roomStatusesLoading ? 'Saving...' : 'Save Room Status Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Appearance
              </CardTitle>
              <CardDescription>
                Customize the look and feel of the application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label>Theme</Label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setTheme('light')}
                    className={`p-4 border rounded-lg text-center transition-all ${
                      theme === 'light' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'hover:border-blue-200'
                    }`}
                    data-testid="theme-light-btn"
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-white border shadow-sm" />
                    <span className="text-sm font-medium">Light</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('dark')}
                    className={`p-4 border rounded-lg text-center transition-all ${
                      theme === 'dark' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'hover:border-blue-200'
                    }`}
                    data-testid="theme-dark-btn"
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-slate-900 border" />
                    <span className="text-sm font-medium">Dark</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('system')}
                    className={`p-4 border rounded-lg text-center transition-all ${
                      theme === 'system' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'hover:border-blue-200'
                    }`}
                    data-testid="theme-system-btn"
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-gradient-to-r from-white to-slate-900 border" />
                    <span className="text-sm font-medium">System</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest Fields */}
        <TabsContent value="guest-fields">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Guest Information Fields
              </CardTitle>
              <CardDescription>
                Choose which fields are required when adding guests. First name and last name are always required.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {GUEST_FIELDS.map((field) => {
                  const isEnabled = guestFields[field.id];
                  
                  return (
                    <div
                      key={field.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isEnabled ? 'bg-muted/50 border-primary/30' : 'bg-background'
                      }`}
                      data-testid={`guest-field-toggle-${field.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {isEnabled ? (
                          <Eye className="h-5 w-5 text-primary" />
                        ) : (
                          <EyeOff className="h-5 w-5 text-muted-foreground" />
                        )}
                        <div>
                          <p className="font-medium">{field.name}</p>
                          <p className="text-sm text-muted-foreground">{field.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => toggleGuestField(field.id)}
                        data-testid={`guest-field-switch-${field.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              
              <Button onClick={handleSaveGuestFields} disabled={guestFieldsLoading} data-testid="save-guest-fields-btn">
                <Save className="h-4 w-4 mr-2" />
                {guestFieldsLoading ? 'Saving...' : 'Save Guest Field Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
              <CardDescription>
                Configure notification preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Receive email alerts for new reservations</p>
                </div>
                <Switch data-testid="email-notifications-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Daily Summary</p>
                  <p className="text-sm text-muted-foreground">Receive daily occupancy and revenue summary</p>
                </div>
                <Switch data-testid="daily-summary-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Maintenance Alerts</p>
                  <p className="text-sm text-muted-foreground">Get notified about urgent maintenance requests</p>
                </div>
                <Switch defaultChecked data-testid="maintenance-alerts-switch" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
            <DialogDescription>
              Select your billing cycle and complete the upgrade
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && subscriptionPlans[selectedPlan] && (
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-muted/50">
                <h3 className="font-semibold text-lg capitalize mb-1">
                  {subscriptionPlans[selectedPlan].name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {subscriptionPlans[selectedPlan].max_hotels === -1 ? 'Unlimited' : subscriptionPlans[selectedPlan].max_hotels} hotels • 
                  {subscriptionPlans[selectedPlan].max_rooms_per_hotel === -1 ? ' Unlimited' : ` ${subscriptionPlans[selectedPlan].max_rooms_per_hotel}`} rooms • 
                  {subscriptionPlans[selectedPlan].max_staff === -1 ? ' Unlimited' : ` ${subscriptionPlans[selectedPlan].max_staff}`} staff
                </p>
              </div>

              <div className="space-y-3">
                <Label>Billing Cycle</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedBillingCycle === 'monthly' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setSelectedBillingCycle('monthly')}
                  >
                    <div className="font-medium">Monthly</div>
                    <div className="text-2xl font-bold">€{subscriptionPlans[selectedPlan].price_monthly}</div>
                    <div className="text-sm text-muted-foreground">per month</div>
                  </div>
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-colors relative ${
                      selectedBillingCycle === 'yearly' ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setSelectedBillingCycle('yearly')}
                  >
                    <Badge className="absolute -top-2 -right-2 bg-emerald-600">Save €{subscriptionPlans[selectedPlan].price_monthly * 12 - subscriptionPlans[selectedPlan].price_yearly}</Badge>
                    <div className="font-medium">Yearly</div>
                    <div className="text-2xl font-bold">€{subscriptionPlans[selectedPlan].price_yearly}</div>
                    <div className="text-sm text-muted-foreground">per year</div>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                  <CreditCard className="h-4 w-4" />
                  <p className="text-sm">
                    <strong>Secure Payment via Stripe:</strong> A new tab will open for checkout. Your subscription activates automatically after payment.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpgrade} data-testid="confirm-upgrade-btn">
              {`Pay €${selectedPlan && subscriptionPlans[selectedPlan] ? 
                (selectedBillingCycle === 'yearly' ? subscriptionPlans[selectedPlan].price_yearly : subscriptionPlans[selectedPlan].price_monthly) 
                : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
